// components/Survey/SurveyCategoryForm.jsx
import React, { useState, useEffect } from 'react';
import { CheckCircleIcon, FolderIcon } from '@heroicons/react/24/solid';
import { motion } from 'framer-motion';

const SurveyCategoryForm = ({ surveyId, onSubmit, onCancel, initialData, isEditing = false }) => {
  const [formData, setFormData] = useState(initialData || {
    name: '',
    description: '',
    surveyId: surveyId,
    order: 0
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isEditing && initialData) {
      setFormData(initialData);
    } else {
      setFormData(prev => ({ ...prev, surveyId }));
    }
  }, [initialData, isEditing, surveyId]);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'number' ? parseInt(value) || 0 : value 
    }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleTextAreaChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Category name is required';
    if (formData.order < 0) newErrors.order = 'Order must be a positive number';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    try {
      await onSubmit(formData);
    } catch (error) {
      setErrors({ general: error.message || 'Error saving category' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col lg:flex-row gap-6 bg-gradient-to-br from-blue-50 via-white to-blue-50 p-6 rounded-2xl shadow-md">
      {/* Left Panel */}
      <div className="w-full lg:w-1/4 bg-gradient-to-br from-blue-100 via-white to-blue-100 rounded-2xl shadow-md p-6 border border-blue-100 h-fit">
        <div className="w-48 h-48 bg-gradient-to-br from-blue-200 to-blue-300 rounded-2xl flex items-center justify-center mb-3 mx-auto">
          <FolderIcon className="w-16 h-16 text-blue-600" />
        </div>
        <h2 className="text-lg font-semibold text-gray-800">Category Settings</h2>
        <p className="text-gray-500 text-sm mt-1 leading-snug">
          {isEditing 
            ? 'Update category details and ordering.' 
            : 'Create a new survey category to organize questions.'}
        </p>
      </div>

      {/* Right Form Panel */}
      <motion.form
        onSubmit={handleSubmit}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="flex-1 bg-gradient-to-br from-blue-50 via-white to-blue-50 rounded-xl shadow-sm p-6 border border-blue-100"
      >
        {errors.general && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg mb-6">
            <p className="text-red-700 text-sm">{errors.general}</p>
          </div>
        )}

        {/* Basic Information */}
        <div className="grid grid-cols-1 gap-6 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Category Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name || ''}
              onChange={handleChange}
              required
              placeholder="Employee Satisfaction"
              className={`w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-blue-400 outline-none shadow-sm text-gray-800 transition-colors ${
                errors.name ? 'border-red-500' : ''
              }`}
            />
            {errors.name && (
              <p className="text-red-500 text-sm mt-1">{errors.name}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description || ''}
              onChange={(e) => handleTextAreaChange('description', e.target.value)}
              rows={3}
              placeholder="Brief description of what this category covers..."
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-blue-400 outline-none shadow-sm text-gray-800 resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Display Order
            </label>
            <input
              type="number"
              name="order"
              value={formData.order || 0}
              onChange={handleChange}
              min="0"
              className={`w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-blue-400 outline-none shadow-sm text-gray-800 ${
                errors.order ? 'border-red-500' : ''
              }`}
            />
            {errors.order && (
              <p className="text-red-500 text-sm mt-1">{errors.order}</p>
            )}
            <p className="text-gray-500 text-xs mt-1">
              Lower numbers appear first. Categories are sorted by this order.
            </p>
          </div>
        </div>

        {/* Quick Tips */}
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-blue-800 mb-2">
            💡 Category Creation Tips
          </h4>
          <ul className="text-blue-700 text-sm space-y-1">
            <li>• Use clear, descriptive names for categories</li>
            <li>• Set appropriate order for logical flow</li>
            <li>• Add questions after creating the category</li>
            <li>• Group related questions together in categories</li>
          </ul>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end gap-4 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center gap-2"
          >
            {isSubmitting ? (
              <>
                <span className="animate-spin border-2 border-white border-t-transparent rounded-full w-4 h-4"></span>
                Saving...
              </>
            ) : (
              <>
                <CheckCircleIcon className="w-5 h-5" />
                {isEditing ? 'Update Category' : 'Create Category'}
              </>
            )}
          </button>
        </div>
      </motion.form>
    </div>
  );
};

export default SurveyCategoryForm;